import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Generic hook to persist filter state in localStorage per view/URL
 * Automatically uses the current URL path as part of the storage key
 *
 * @param filterDefaults - Default values for all filters
 * @param storageKeyPrefix - Optional prefix for the storage key (default: 'filter')
 * @param routeIndependentKey - When set, this replaces the URL path in the storage
 *   key so a view rendered on more than one route shares ONE saved filter set.
 *   The Task Queue needs it: the same component is the `/tasks` page and a drawer
 *   openable from every queue, and a filter chosen in the drawer must still be
 *   there on `/tasks` (GP-1337). Omit it for single-route views.
 * @returns Tuple of [filters, updateFilter, resetFilters]
 *
 * @example
 * const [filters, updateFilter, resetFilters] = useFilterPersistence({
 *   dateFilter: 'today',
 *   searchTerm: '',
 *   statusFilter: 'all'
 * });
 *
 * // Update a single filter
 * updateFilter('dateFilter', 'tomorrow');
 *
 * // Reset all filters to defaults
 * resetFilters();
 */
export function useFilterPersistence<T extends Record<string, any>>(
  filterDefaults: T,
  storageKeyPrefix: string = 'filter',
  routeIndependentKey?: string
): [T, (key: keyof T, value: T[keyof T]) => void, () => void] {
  const location = useLocation();

  // Create a unique storage key based on the current URL path
  const storageKey = `${storageKeyPrefix}:${routeIndependentKey ?? location.pathname}`;

  // Helper function to load filters from localStorage
  const loadFiltersFromStorage = useCallback((): T => {
    try {
      const storedFilters = localStorage.getItem(storageKey);
      if (storedFilters) {
        const parsed = JSON.parse(storedFilters);
        // Merge stored filters with defaults to ensure all keys exist
        return { ...filterDefaults, ...parsed };
      }
    } catch (error) {
      console.error(`Error loading filters from localStorage (${storageKey}):`, error);
    }
    return filterDefaults;
  }, [storageKey, filterDefaults]);

  // Initialize state with stored values or defaults
  const [filters, setFilters] = useState<T>(loadFiltersFromStorage);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (error) {
      console.error(`Error saving filters to localStorage (${storageKey}):`, error);
    }
  }, [filters, storageKey]);

  // Update a single filter value
  const updateFilter = useCallback((key: keyof T, value: T[keyof T]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setFilters(filterDefaults);
  }, [filterDefaults]);

  return [filters, updateFilter, resetFilters];
}
